import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _classPrivateFieldGet from '@babel/runtime/helpers/esm/classPrivateFieldGet';
import _classPrivateFieldSet from '@babel/runtime/helpers/esm/classPrivateFieldSet';
import { ExtensionTag, object, values, includes, NodeExtension, extensionDecorator, convertCommand, nonChainable, presetDecorator, Preset, ExtensionPriority } from '@remirror/core';
import { TextSelection } from '@remirror/pm/state';
import { fixTablesKey, tableEditing, columnResizing, deleteTable, addColumnBefore, addColumnAfter, deleteColumn, addRowBefore, addRowAfter, deleteRow, mergeCells, splitCell, toggleHeaderColumn, toggleHeaderRow, toggleHeaderCell, setCellAttr, fixTables } from '@remirror/pm/tables';
import _createForOfIteratorHelper from '@babel/runtime/helpers/esm/createForOfIteratorHelper';

// Helper for creating a schema that supports tables.
function getCellAttrs(dom) {
  var _dom$getAttribute, _dom$getAttribute2;

  var widthAttr = dom.getAttribute('data-colwidth');
  var widths = widthAttr && /^\d+(,\d+)*$/.test(widthAttr) ? widthAttr.split(',').map(s => Number(s)) : null;
  var colspan = Number((_dom$getAttribute = dom.getAttribute('colspan')) !== null && _dom$getAttribute !== void 0 ? _dom$getAttribute : 1);
  return {
    colspan,
    rowspan: Number((_dom$getAttribute2 = dom.getAttribute('rowspan')) !== null && _dom$getAttribute2 !== void 0 ? _dom$getAttribute2 : 1),
    colwidth: widths && widths.length === colspan ? widths : null,
    background: dom.style.backgroundColor || null
  };
}

function setCellAttrs(node) {
  var attrs = {};

  if (node.attrs.colspan !== 1) {
    attrs.colspan = node.attrs.colspan;
  }

  if (node.attrs.rowspan !== 1) {
    attrs.rowspan = node.attrs.rowspan;
  }

  if (node.attrs.colwidth) {
    attrs['data-colwidth'] = node.attrs.colwidth.join(',');
  }

  if (node.attrs.background) {
    attrs.style = "".concat(attrs.style || '', "background-color: ").concat(node.attrs.background, ";");
  }

  return attrs;
}
/**
 * This function creates the base for the tableNode ProseMirror specs.
 */


function createTableNodeSchema(extra) {
  var cellAttrs = _objectSpread(_objectSpread({}, extra.defaults()), {}, {
    colspan: {
      default: 1
    },
    rowspan: {
      default: 1
    },
    colwidth: {
      default: null
    },
    background: {
      default: null
    }
  });

  return {
    table: {
      attrs: extra.defaults(),
      content: 'tableRow+',
      tableRole: 'table',
      isolating: true,
      parseDOM: [{
        tag: 'table',
        getAttrs: extra.parse
      }],

      toDOM(node) {
        return ['table', ['tbody', extra.dom(node), 0]];
      }

    },
    tableRow: {
      attrs: extra.defaults(),
      content: '(tableCell | tableHeaderCell)*',
      tableRole: 'row',
      parseDOM: [{
        tag: 'tr',
        getAttrs: extra.parse
      }],

      toDOM(node) {
        return ['tr', extra.dom(node), 0];
      }

    },
    tableCell: {
      content: "".concat(ExtensionTag.BlockNode, "+"),
      attrs: cellAttrs,
      tableRole: 'cell',
      isolating: true,
      parseDOM: [{
        tag: 'td',
        getAttrs: dom => _objectSpread(_objectSpread({}, extra.parse(dom)), getCellAttrs(dom))
      }],

      toDOM(node) {
        return ['td', _objectSpread(_objectSpread({}, extra.dom(node)), setCellAttrs(node)), 0];
      }

    },
    tableHeaderCell: {
      content: "".concat(ExtensionTag.BlockNode, "+"),
      attrs: cellAttrs,
      tableRole: 'header_cell',
      isolating: true,
      parseDOM: [{
        tag: 'th',
        getAttrs: dom => _objectSpread(_objectSpread({}, extra.parse(dom)), getCellAttrs(dom))
      }],

      toDOM(node) {
        return ['th', _objectSpread(_objectSpread({}, extra.dom(node)), setCellAttrs(node)), 0];
      }

    }
  };
}
var TABLE_ROLES = ['table', 'row', 'cell', 'header_cell'];

/**
 * Returns a map where keys are tableRoles and values are NodeTypes.
 */
function tableNodeTypes(schema) {
  if (schema.cached.tableNodeTypes) {
    return schema.cached.tableNodeTypes;
  }

  var roles = object();
  schema.cached.tableNodeTypes = roles;

  var _iterator = _createForOfIteratorHelper(values(schema.nodes)),
      _step;

  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var nodeType = _step.value;

      if (includes(TABLE_ROLES, nodeType.spec.tableRole)) {
        roles[nodeType.spec.tableRole] = nodeType;
      }
    }
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }

  return roles;
}

/**
 * Create a cell with the provided content.
 */
function createCell(parameter) {
  var content = parameter.content,
      type = parameter.type;

  if (content) {
    return type.createChecked(null, content);
  }

  return type.createAndFill();
}
/**
 * Returns a table node of a given size.
 *
 * @remarks
 *
 * ```ts
 * const table = createTable({ schema: state.schema }); // 3x3 table node
 * dispatch(tr.replaceSelectionWith(table).scrollIntoView());
 * ```
 */


function createTable(parameter) {
  var schema = parameter.schema,
      cellContent = parameter.cellContent,
      _parameter$columnsCou = parameter.columnsCount,
      columnsCount = _parameter$columnsCou === void 0 ? 3 : _parameter$columnsCou,
      _parameter$rowsCount = parameter.rowsCount,
      rowsCount = _parameter$rowsCount === void 0 ? 3 : _parameter$rowsCount,
      _parameter$withHeader = parameter.withHeaderRow,
      withHeaderRow = _parameter$withHeader === void 0 ? true : _parameter$withHeader;

  var _tableNodeTypes = tableNodeTypes(schema),
      tableCell = _tableNodeTypes.cell,
      tableHeaderCell = _tableNodeTypes.header_cell,
      tableRow = _tableNodeTypes.row,
      table = _tableNodeTypes.table;

  var cells = [];
  var headerCells = [];

  for (var ii = 0; ii < columnsCount; ii++) {
    cells.push(createCell({
      type: tableCell,
      content: cellContent
    }));

    if (withHeaderRow) {
      headerCells.push(createCell({
        type: tableHeaderCell,
        content: cellContent
      }));
    }
  }

  var rows = [];

  for (var _ii = 0; _ii < rowsCount; _ii++) {
    var rowNodes = withHeaderRow && _ii === 0 ? headerCells : cells;
    rows.push(tableRow.createChecked(null, rowNodes));
  }

  return table.createChecked(null, rows);
}

var _dec, _class, _lastGoodState, _temp, _dec2, _class3, _dec3, _class4, _dec4, _class5;
var tablesEnabled = false;
var TableExtension = (_dec = /*#__PURE__*/extensionDecorator({
  defaultOptions: {
    resizable: true
  }
}), /*#__PURE__*/_dec(_class = (_temp = (_lastGoodState = /*#__PURE__*/new WeakMap(), class TableExtension extends NodeExtension {
  constructor() {
    super(...arguments);

    _defineProperty(this, "tags", [ExtensionTag.BlockNode]);

    _lastGoodState.set(this, {
      writable: true,
      value: undefined
    });
  }

  get name() {
    return 'table';
  }

  createNodeSpec(extra) {
    return createTableNodeSchema(extra).table;
  }

  onStateUpdate(parameter) {
    var _tr$getMeta;

    var tr = parameter.tr,
        state = parameter.state;

    if (tr === null || tr === void 0 ? void 0 : (_tr$getMeta = tr.getMeta(fixTablesKey)) === null || _tr$getMeta === void 0 ? void 0 : _tr$getMeta.fixTables) {
      _classPrivateFieldSet(this, _lastGoodState, state);
    }
  }
  /**
   * Add the table plugins to the editor.
   */


  createExternalPlugins() {
    return [tableEditing(), columnResizing({})];
  }
  /**
   * Create the commands that can be used for the table.
   */


  createCommands() {
    return {
      /**
       * Create a table in the editor at the current selection point.
       */
      createTable: parameter => (_ref) => {
        var tr = _ref.tr,
            dispatch = _ref.dispatch,
            state = _ref.state;

        if (!tr.selection.empty) {
          return false;
        }

        var offset = tr.selection.anchor + 1;

        var nodes = createTable(_objectSpread({
          schema: state.schema
        }, parameter));

        dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.replaceSelectionWith(nodes).scrollIntoView().setSelection(TextSelection.near(tr.doc.resolve(offset))));
        return true;
      },

      /**
       * Delete the table.
       */
      deleteTable: () => convertCommand(deleteTable),

      /**
       * Command to add a column before the column with the selection.
       */
      addTableColumnBefore: () => {
        return convertCommand(addColumnBefore);
      },

      /**
       * Command to add a column after the column with the selection.
       */
      addTableColumnAfter: () => convertCommand(addColumnAfter),

      /**
       * Remove selected column from the table.
       */
      deleteTableColumn: () => convertCommand(deleteColumn),

      /**
       * Add a table row before the current selection.
       */
      addTableRowBefore: () => convertCommand(addRowBefore),

      /**
       * Add a table row after the current selection.
       */
      addTableRowAfter: () => convertCommand(addRowAfter),

      /**
       * Delete the table row at the current selection.
       */
      deleteTableRow: () => convertCommand(deleteRow),

      /**
       * Toggles between merging cells.
       */
      toggleTableCellMerge: () => toggleMergeCellCommand,

      /**
       * Merge the table cells.
       */
      mergeTableCells: () => convertCommand(mergeCells),

      /**
       * Split the merged cells into individual cells.
       */
      splitTableCell: () => convertCommand(splitCell),

      /**
       * Toggles a column as the header column.
       */
      toggleTableHeaderColumn: () => convertCommand(toggleHeaderColumn),

      /**
       * Toggles a row as a table header row.
       */
      toggleTableHeaderRow: () => convertCommand(toggleHeaderRow),

      /**
       * Toggle a cell as a table header cell.
       */
      toggleTableHeaderCell: () => convertCommand(toggleHeaderCell),

      /**
       * Set the attribute for a table cell.
       */
      setTableCellAttribute: (name, value) => convertCommand(setCellAttr(name, value)),

      /**
       * Fix all tables within the document.
       *
       * This is a **non-chainable** command.
       */
      fixTables: () => nonChainable(fixTablesCommand(_classPrivateFieldGet(this, _lastGoodState)))
    };
  }

  createHelpers() {
    return {
      /**
       * Enable table usage within the editor. This depends on the browser that
       * is being used.
       */
      enableTableSupport: () => {
        if (tablesEnabled) {
          return;
        }

        document.execCommand('enableObjectResizing', false, 'false');
        document.execCommand('enableInlineTableEditing', false, 'false');
        tablesEnabled = true;
      }
    };
  }
  /**
   * This managers the updates of the collaboration provider.
   */


  onSetOptions(parameter) {
    var changes = parameter.changes; // TODO move this into a new method in `plugins-extension`.

    if (changes.resizable.changed) {
      var previousPlugins = this.externalPlugins;
      var newPlugins = this.externalPlugins = this.createExternalPlugins();
      this.store.addOrReplacePlugins(newPlugins, previousPlugins);
      this.store.reconfigureStatePlugins();
    }
  }

}), _temp)) || _class);
/**
 * The extension for a table row node.
 */

var TableRowExtension = (_dec2 = /*#__PURE__*/extensionDecorator({}), /*#__PURE__*/_dec2(_class3 = class TableRowExtension extends NodeExtension {
  get name() {
    return 'tableRow';
  }

  createNodeSpec(extra) {
    return createTableNodeSchema(extra).tableRow;
  }

}) || _class3);
/**
 * The extension for a table cell node.
 */

var TableCellExtension = (_dec3 = /*#__PURE__*/extensionDecorator({}), /*#__PURE__*/_dec3(_class4 = class TableCellExtension extends NodeExtension {
  get name() {
    return 'tableCell';
  }

  createNodeSpec(extra) {
    return createTableNodeSchema(extra).tableCell;
  }

}) || _class4);
/**
 * The extension for the table header node.
 */

var TableHeaderCellExtension = (_dec4 = /*#__PURE__*/extensionDecorator({}), /*#__PURE__*/_dec4(_class5 = class TableHeaderCellExtension extends NodeExtension {
  get name() {
    return 'tableHeaderCell';
  }

  createNodeSpec(extra) {
    return createTableNodeSchema(extra).tableHeaderCell;
  }

}) || _class5);
/**
 * The command for fixing the tables.
 */

function fixTablesCommand(lastGoodState) {
  return (_ref2) => {
    var state = _ref2.state,
        dispatch = _ref2.dispatch;
    var tr = fixTables(state, lastGoodState);

    if (!tr) {
      return false;
    }

    if (dispatch) {
      dispatch(tr);
    }

    return true;
  };
}

function toggleMergeCellCommand(_ref3) {
  var state = _ref3.state,
      dispatch = _ref3.dispatch;

  if (mergeCells(state, dispatch)) {
    return false;
  }

  return splitCell(state, dispatch);
}

var _dec$1, _class$1;
/**
 * The table is packaged up as preset for simpler consumption.
 */

var TablePreset = (_dec$1 = /*#__PURE__*/presetDecorator({
  defaultOptions: TableExtension.defaultOptions
}), /*#__PURE__*/_dec$1(_class$1 = class TablePreset extends Preset {
  get name() {
    return 'table';
  }
  /**
   * Create the table extensions. Set the priority to low so that they appear
   * lower down in the node list.
   */


  createExtensions() {
    return [new TableExtension({
      priority: ExtensionPriority.Low,
      resizable: this.options.resizable
    }), new TableRowExtension({
      priority: ExtensionPriority.Low
    }), new TableCellExtension({
      priority: ExtensionPriority.Low
    }), new TableHeaderCellExtension({
      priority: ExtensionPriority.Low
    })];
  }

  onSetOptions(parameter) {
    var changes = parameter.changes;

    if (changes.resizable.changed) {
      this.getExtension(TableExtension).setOptions({
        resizable: changes.resizable.value
      });
    }
  }

}) || _class$1);

export { TableCellExtension, TableExtension, TableHeaderCellExtension, TablePreset, TableRowExtension };
